import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { clientsApi } from '../services/api'
import { useNotificationStore } from './notifications'

export const useClientStore = defineStore('clients', () => {
  // State
  const clients = ref([])
  const currentClient = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const pagination = ref({
    total: 0,
    page: 1,
    size: 20,
    pages: 1
  })
  const filters = ref({
    nombre: '',
    cuit: '',
    active: null
  })

  // Getters
  const totalClients = computed(() => pagination.value.total)
  const activeClients = computed(() =>
    clients.value.filter(client => client.active).length
  )
  const inactiveClients = computed(() =>
    clients.value.filter(client => !client.active).length
  )

  // Actions
  const notificationStore = useNotificationStore()

  const setLoading = (isLoading) => {
    loading.value = isLoading
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
    if (errorMessage) {
      notificationStore.addNotification('error', errorMessage)
    }
  }

  const clearError = () => {
    error.value = null
  }

  const fetchClients = async (page = 1, size = 20) => {
    setLoading(true)
    clearError()

    try {
      const params = {
        page,
        size,
        ...filters.value
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key]
        }
      })

      const response = await clientsApi.getClients(params)

      clients.value = response.items
      pagination.value = {
        total: response.total,
        page: response.page,
        size: response.size,
        pages: response.pages
      }

      return response
    } catch (error) {
      setError('Error al cargar los clientes: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const fetchClient = async (id) => {
    setLoading(true)
    clearError()

    try {
      const client = await clientsApi.getClient(id)
      currentClient.value = client
      return client
    } catch (error) {
      setError('Error al cargar el cliente: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createClient = async (clientData) => {
    setLoading(true)
    clearError()

    try {
      const newClient = await clientsApi.createClient(clientData)

      // Add to local state if on first page
      if (pagination.value.page === 1) {
        clients.value.unshift(newClient)
        // Remove last item if exceeding page size
        if (clients.value.length > pagination.value.size) {
          clients.value.pop()
        }
      }

      // Update pagination
      pagination.value.total += 1

      notificationStore.addNotification('success', 'Cliente creado exitosamente')
      return newClient
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al crear el cliente: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateClient = async (id, clientData) => {
    setLoading(true)
    clearError()

    try {
      const updatedClient = await clientsApi.updateClient(id, clientData)

      // Update in local state
      const index = clients.value.findIndex(client => client.id === id)
      if (index !== -1) {
        clients.value[index] = updatedClient
      }

      // Update current client if it's the same
      if (currentClient.value && currentClient.value.id === id) {
        currentClient.value = updatedClient
      }

      notificationStore.addNotification('success', 'Cliente actualizado exitosamente')
      return updatedClient
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al actualizar el cliente: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteClient = async (id) => {
    setLoading(true)
    clearError()

    try {
      await clientsApi.deleteClient(id)

      // Remove from local state
      clients.value = clients.value.filter(client => client.id !== id)

      // Update pagination
      pagination.value.total -= 1

      notificationStore.addNotification('success', 'Cliente eliminado exitosamente')

      // Clear current client if it's the deleted one
      if (currentClient.value && currentClient.value.id === id) {
        currentClient.value = null
      }

      return true
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al eliminar el cliente: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const searchClientByCuit = async (cuit) => {
    setLoading(true)
    clearError()

    try {
      const client = await clientsApi.getClientByCuit(cuit)
      return client
    } catch (error) {
      if (error.response?.status === 404) {
        return null // Client not found
      }
      setError('Error al buscar el cliente: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getClientStats = async (id) => {
    try {
      const stats = await clientsApi.getClientStats(id)
      return stats
    } catch (error) {
      setError('Error al obtener estadísticas del cliente: ' + error.message)
      throw error
    }
  }

  const fetchClientDevices = async (id, activeOnly = false) => {
    try {
      const devices = await clientsApi.getClientDevices(id, activeOnly)
      return devices
    } catch (error) {
      setError('Error al obtener los dispositivos del cliente: ' + error.message)
      throw error
    }
  }

  const fetchDeviceReports = async (clientId, deviceId) => {
    try {
      const reports = await clientsApi.getDeviceReports(clientId, deviceId)
      return reports
    } catch (error) {
      setError('Error al obtener los reportes del dispositivo: ' + error.message)
      throw error
    }
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const clearFilters = () => {
    filters.value = {
      nombre: '',
      cuit: '',
      active: null
    }
  }

  const goToPage = async (page) => {
    if (page >= 1 && page <= pagination.value.pages) {
      await fetchClients(page, pagination.value.size)
    }
  }

  const changePageSize = async (size) => {
    await fetchClients(1, size)
  }

  const refreshClients = async () => {
    await fetchClients(pagination.value.page, pagination.value.size)
  }

  // Reset store
  const $reset = () => {
    clients.value = []
    currentClient.value = null
    loading.value = false
    error.value = null
    pagination.value = {
      total: 0,
      page: 1,
      size: 20,
      pages: 1
    }
    filters.value = {
      nombre: '',
      cuit: '',
      active: null
    }
  }

  return {
    // State
    clients,
    currentClient,
    loading,
    error,
    pagination,
    filters,

    // Getters
    totalClients,
    activeClients,
    inactiveClients,

    // Actions
    fetchClients,
    fetchClient,
    createClient,
    updateClient,
    deleteClient,
    searchClientByCuit,
    getClientStats,
    fetchClientDevices,
    fetchDeviceReports,
    setFilters,
    clearFilters,
    goToPage,
    changePageSize,
    refreshClients,
    clearError,
    $reset
  }
})